/**
 * AYU Feedback — Freude / Anmerkung / Fehlfunktion / Fehlfunktion kritisch
 * 
 * Ersetzt die alte "Fehler melden"-Seite mit 4 Kategorien:
 * 1. Freude — Was gefällt besonders gut
 * 2. Anmerkung — Verbesserungsvorschlag oder Hinweis
 * 3. Fehlfunktion — Etwas funktioniert nicht richtig
 * 4. Fehlfunktion kritisch — App nicht nutzbar oder Datenverlust
 */
import { useState, useRef } from "react";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  Heart,
  MessageSquare,
  AlertTriangle,
  Flame,
  Camera,
  X,
  Send,
  Loader2,
  CheckCircle2,
  ArrowLeft,
  MessageSquarePlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

type FeedbackCategory = "freude" | "anmerkung" | "fehlfunktion" | "fehlfunktion_kritisch";

const CATEGORY_OPTIONS_BASE: {
  value: FeedbackCategory;
  labelKey: "freude" | "anmerkung" | "fehlfunktion" | "fehlfunktionKritisch";
  descKey: "freudeDesc" | "anmerkungDesc" | "fehlfunktionDesc" | "fehlfunktionKritischDesc";
  icon: typeof Heart;
  color: string;
  bg: string;
  border: string;
  ring: string;
  severityMap: "low" | "medium" | "high" | "critical";
}[] = [
  {
    value: "freude",
    labelKey: "freude",
    descKey: "freudeDesc",
    icon: Heart,
    color: "text-pink-600",
    bg: "bg-pink-50",
    border: "border-pink-200",
    ring: "ring-pink-300",
    severityMap: "low",
  },
  {
    value: "anmerkung",
    labelKey: "anmerkung",
    descKey: "anmerkungDesc",
    icon: MessageSquare,
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    ring: "ring-blue-300",
    severityMap: "medium",
  },
  {
    value: "fehlfunktion",
    labelKey: "fehlfunktion",
    descKey: "fehlfunktionDesc",
    icon: AlertTriangle,
    color: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-200",
    ring: "ring-orange-300",
    severityMap: "high",
  },
  {
    value: "fehlfunktion_kritisch",
    labelKey: "fehlfunktionKritisch",
    descKey: "fehlfunktionKritischDesc",
    icon: Flame,
    color: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
    ring: "ring-red-300",
    severityMap: "critical",
  },
];

const PAGE_OPTIONS_STATIC = [
  { value: "/app/dashboard", label: "Dashboard" },
  { value: "/app/tagebuch", label: "Tagebuch" },
  { value: "/app/coach", label: "Annam" },
  { value: "/app/sessions", label: "Sessions" },
  { value: "/app/kompass", label: "Kompass" },
  { value: "/app/programme", label: "Programme" },
  { value: "/app/fortschritt", label: "Fortschritte" },
  { value: "/app/gamification", label: "Gamification" },
  { value: "/app/wearables", label: "Wearables" },
  { value: "/app/community", label: "Community" },
  { value: "/app/dokumente", label: "Dokumente" },
  { value: "/app/schlaf", label: "Schlaf" },
  { value: "/app/ernaehrungsprofil", label: "Ernährung" },
  { value: "/app/tagesroutine", label: "Tagesroutine" },
  { value: "/app/rezepte", label: "Rezepte" },
  { value: "/app/profil", label: "Profil" },
  { value: "/app/hilfe", label: "Hilfe & FAQ" },
];

export default function AppFeedback() {
  const { client } = useClientAuth();
  const { t } = useI18n();
  const submitMutation = trpc.bugReport.submitForm.useMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const CATEGORY_OPTIONS = CATEGORY_OPTIONS_BASE.map(opt => ({
    ...opt,
    label: t(`feedback.${opt.labelKey}`),
    description: t(`feedback.${opt.descKey}`),
  }));

  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [affectedPage, setAffectedPage] = useState("");
  const [customPage, setCustomPage] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [detailedDescription, setDetailedDescription] = useState("");
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [screenshotBase64, setScreenshotBase64] = useState<string | null>(null);
  const [screenshotMimeType, setScreenshotMimeType] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const selectedCategory = CATEGORY_OPTIONS.find((c) => c.value === category);

  const handleScreenshot = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("feedback.bildZuGross"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setScreenshotPreview(result);
      const base64 = result.split(",")[1];
      setScreenshotBase64(base64);
      setScreenshotMimeType(file.type);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!category || !shortDescription || !detailedDescription) {
      toast.error(t("feedback.pflichtfelder"));
      return;
    }
    // For Freude/Anmerkung, page is optional
    const needsPage = category === "fehlfunktion" || category === "fehlfunktion_kritisch";
    if (needsPage && !affectedPage) {
      toast.error(t("feedback.seitePflicht"));
      return;
    }
    const page = affectedPage === "andere" ? customPage || "Andere" : affectedPage || "Allgemein";
    const categoryLabel = selectedCategory?.label || category;

    try {
      const result = await submitMutation.mutateAsync({
        clientId: client?.clientId,
        clientName: client?.firstName || undefined,
        severity: selectedCategory?.severityMap || "medium",
        affectedPage: page,
        shortDescription: `[${categoryLabel}] ${shortDescription}`,
        detailedDescription: `**Kategorie:** ${categoryLabel}\n**Betroffene Seite:** ${page}\n\n**Kurzbeschreibung:** ${shortDescription}\n\n**Detaillierte Beschreibung:**\n${detailedDescription}`,
        screenshotBase64: screenshotBase64 || undefined,
        screenshotMimeType: screenshotMimeType || undefined,
      });
      setSubmitted(true);
      const successMsg = category === "freude"
        ? t("feedback.dankeFreude")
        : category === "anmerkung"
          ? t("feedback.dankeAnmerkung")
          : t("feedback.dankeFehler");
      toast.success(successMsg);

      // Fire-and-forget: sync to Plane — never blocks UX if Plane is unavailable
      const planeType = (category === "fehlfunktion" || category === "fehlfunktion_kritisch")
        ? "bug" : "improvement";
      const planePriority = selectedCategory?.severityMap === "critical"
        ? "urgent" : (selectedCategory?.severityMap ?? "none");
      fetch("/api/plane/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          application: "ayu",
          type: planeType,
          title: shortDescription,
          description: detailedDescription,
          priority: planePriority,
          context: { page, category: categoryLabel },
          metadata: {
            screenshotUrl: result.screenshotUrl ?? undefined,
            originalId: result.reportId ?? undefined,
          },
        }),
      }).catch((err) => {
        console.warn("[Feedback] Plane sync failed:", err);
      });
    } catch (err: any) {
      toast.error(err.message || t("feedback.sendeError"));
    }
  };

  const handleReset = () => {
    setCategory(null);
    setAffectedPage("");
    setCustomPage("");
    setShortDescription("");
    setDetailedDescription("");
    setScreenshotPreview(null);
    setScreenshotBase64(null);
    setScreenshotMimeType(null);
    setSubmitted(false);
  };

  if (submitted) {
    const isPositive = category === "freude" || category === "anmerkung";
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl border border-[#e8e0d4] p-8 text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isPositive ? "bg-pink-100" : "bg-green-100"}`}>
            {isPositive ? (
              <Heart className="w-8 h-8 text-pink-600" />
            ) : (
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            )}
          </div>
          <h2 className="text-xl font-bold text-[#2d3a1a] font-['Cormorant_Garamond'] mb-2">
            {isPositive ? t("feedback.dankePositivTitle") : t("feedback.dankeFehlerTitle")}
          </h2>
          <p className="text-sm text-[#6b7c5a] mb-6">
            {isPositive
              ? t("feedback.dankePositivDesc")
              : t("feedback.dankeFehlerDesc")}
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={handleReset}
              variant="outline"
              className="border-[#d4c9a8] text-[#5a6b3c] hover:bg-[#f5f0e8]"
            >
              {t("feedback.weiteresFeedbackBtn")}
            </Button>
            <Link href="/hilfe">
              <Button className="bg-[#5a6b3c] hover:bg-[#4a5b2c] text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t("feedback.zurueckHilfe")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Dynamic labels based on category
  const isPositiveCategory = category === "freude" || category === "anmerkung";
  const shortPlaceholder = category === "freude"
    ? t("feedback.shortPlaceholderFreude")
    : category === "anmerkung"
      ? t("feedback.shortPlaceholderAnmerkung")
      : t("feedback.shortPlaceholderFehler");
  const detailPlaceholder = category === "freude"
    ? t("feedback.detailPlaceholderFreude")
    : category === "anmerkung"
      ? t("feedback.detailPlaceholderAnmerkung")
      : t("feedback.detailPlaceholderFehler");

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Link href="/hilfe">
            <button className="w-8 h-8 rounded-lg hover:bg-[#f5f0e8] flex items-center justify-center transition-colors">
              <ArrowLeft className="w-4 h-4 text-[#6b7c5a]" />
            </button>
          </Link>
          <MessageSquarePlus className="w-5 h-5 text-[#C9A96E]" />
          <h1 className="text-xl font-bold text-[#2d3a1a] font-['Cormorant_Garamond']">
            {t("feedback.feedbackTitle")}
          </h1>
        </div>
        <p className="text-sm text-[#6b7c5a] ml-10">
          {t("feedback.feedbackSubtitle")}
        </p>
      </div>

      <div className="space-y-6">
        {/* 1. Kategorie */}
        <div className="bg-white rounded-xl border border-[#e8e0d4] p-5">
          <label className="block text-sm font-semibold text-[#2d3a1a] mb-3">
            1. {t("feedback.wasNachricht")} <span className="text-red-400">{t("feedback.pflichtMarkierung")}</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CATEGORY_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isSelected = category === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setCategory(opt.value)}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                    isSelected
                      ? `${opt.border} ${opt.bg} ring-2 ${opt.ring}`
                      : "border-[#e8e0d4] hover:border-[#d4c9a8] bg-[#faf8f4]"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg ${opt.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <Icon className={`w-4 h-4 ${opt.color}`} />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isSelected ? opt.color : "text-[#2d3a1a]"}`}>
                      {opt.label}
                    </p>
                    <p className="text-xs text-[#8a7d65] mt-0.5">{opt.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* 2. Betroffene Seite (required for Fehlfunktion, optional for Freude/Anmerkung) */}
        {category && (
          <div className="bg-white rounded-xl border border-[#e8e0d4] p-5">
            <label className="block text-sm font-semibold text-[#2d3a1a] mb-3">
              2. {t("feedback.seitePflichtLabel")}{" "}
              {!isPositiveCategory && <span className="text-red-400">*</span>}
              {isPositiveCategory && <span className="text-xs font-normal text-[#8a7d65]">{t("feedback.optional")}</span>}
            </label>
            <select
              value={affectedPage}
              onChange={(e) => setAffectedPage(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-[#e8e0d4] bg-[#faf8f4] text-sm text-[#2d3a1a] focus:outline-none focus:ring-2 focus:ring-[#5a6b3c]/30 focus:border-[#5a6b3c]"
            >
              <option value="">{t("feedback.seiteAuswaehlen")}</option>
              {PAGE_OPTIONS_STATIC.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
              <option value="allgemein">{t("feedback.allgemein")}</option>
              <option value="andere">{t("feedback.anderesSeite")}</option>
            </select>
            {affectedPage === "andere" && (
              <input
                type="text"
                value={customPage}
                onChange={(e) => setCustomPage(e.target.value)}
                placeholder={t("feedback.sonstSeite")}
                className="w-full mt-2 px-4 py-2.5 rounded-xl border border-[#e8e0d4] bg-[#faf8f4] text-sm text-[#2d3a1a] placeholder:text-[#b5a890] focus:outline-none focus:ring-2 focus:ring-[#5a6b3c]/30 focus:border-[#5a6b3c]"
              />
            )}
          </div>
        )}

        {/* 3. Kurzbeschreibung */}
        {category && (
          <div className="bg-white rounded-xl border border-[#e8e0d4] p-5">
            <label className="block text-sm font-semibold text-[#2d3a1a] mb-3">
              3. {t("feedback.kurzbeschreibungLabel")} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              placeholder={shortPlaceholder}
              maxLength={512}
              className="w-full px-4 py-2.5 rounded-xl border border-[#e8e0d4] bg-[#faf8f4] text-sm text-[#2d3a1a] placeholder:text-[#b5a890] focus:outline-none focus:ring-2 focus:ring-[#5a6b3c]/30 focus:border-[#5a6b3c]"
            />
            <p className="text-xs text-[#8a7d65] mt-1 text-right">{shortDescription.length}/512</p>
          </div>
        )}

        {/* 4. Detaillierte Beschreibung */}
        {category && (
          <div className="bg-white rounded-xl border border-[#e8e0d4] p-5">
            <label className="block text-sm font-semibold text-[#2d3a1a] mb-3">
              4. {t("feedback.detailliertLabel")} <span className="text-red-400">*</span>
            </label>
            <textarea
              value={detailedDescription}
              onChange={(e) => setDetailedDescription(e.target.value)}
              placeholder={detailPlaceholder}
              maxLength={5000}
              rows={5}
              className="w-full px-4 py-3 rounded-xl border border-[#e8e0d4] bg-[#faf8f4] text-sm text-[#2d3a1a] placeholder:text-[#b5a890] focus:outline-none focus:ring-2 focus:ring-[#5a6b3c]/30 focus:border-[#5a6b3c] resize-none"
            />
            <p className="text-xs text-[#8a7d65] mt-1 text-right">{detailedDescription.length}/5000</p>
          </div>
        )}

        {/* 5. Screenshot (optional) */}
        {category && (
          <div className="bg-white rounded-xl border border-[#e8e0d4] p-5">
            <label className="block text-sm font-semibold text-[#2d3a1a] mb-3">
              5. {t("feedback.screenshotOptional")} <span className="text-xs font-normal text-[#8a7d65]">{t("feedback.optional")}</span>
            </label>
            {screenshotPreview ? (
              <div className="relative">
                <img
                  src={screenshotPreview}
                  alt={t("feedback.screenshotVorschau")}
                  className="max-w-full max-h-48 rounded-xl border border-[#e8e0d4] object-contain"
                />
                <button
                  onClick={() => {
                    setScreenshotPreview(null);
                    setScreenshotBase64(null);
                    setScreenshotMimeType(null);
                  }}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-8 rounded-xl border-2 border-dashed border-[#d4c9a8] hover:border-[#5a6b3c] bg-[#faf8f4] hover:bg-[#f5f0e8] transition-all flex flex-col items-center gap-2"
              >
                <Camera className="w-6 h-6 text-[#8a7d65]" />
                <span className="text-sm text-[#6b7c5a]">{t("feedback.klickeOderZiehe")}</span>
                <span className="text-xs text-[#8a7d65]">{t("feedback.maxDateigroesse")}</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleScreenshot}
              className="hidden"
            />
          </div>
        )}

        {/* Submit */}
        {category && (
          <div className="flex justify-end gap-3 pb-6">
            <Link href="/hilfe">
              <Button
                variant="outline"
                className="border-[#d4c9a8] text-[#6b7c5a] hover:bg-[#f5f0e8]"
              >
                {t("feedback.abbrechen")}
              </Button>
            </Link>
            <Button
              onClick={handleSubmit}
              disabled={!category || !shortDescription || !detailedDescription || submitMutation.isPending}
              className={`text-white disabled:opacity-50 ${
                isPositiveCategory
                  ? "bg-[#5a6b3c] hover:bg-[#4a5b2c]"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {submitMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {isPositiveCategory ? t("feedback.absenden") : t("feedback.meldungSenden")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
